import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import { Box, Button, IconButton, List, ListItemButton, Paper, Popper, ClickAwayListener, Switch, Tooltip, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AddCircle } from 'iconsax-react';
import { useMemo, useState } from 'react';
import React from 'react';
import { useRef } from 'react';
import FormFieldsMenu from '@ui/pages/Events/CreateEvent/components/DynamicForm/components/FormFieldsMenu';
import useQuestionCardStyles from './useQuestionCardStyles';
import EditableField from '@ui/pages/Events/CreateEvent/components/DynamicForm/components/EditableField';
import UndertakingField from '@ui/pages/Events/CreateEvent/components/DynamicForm/components/UndertakingField';
import { Asterisk } from '@ui/assets/custom-icons/star-icon';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import InfoIconTooltip from '@ui/components/common/Tooltips/InfoIconTooltip';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getFieldLabelSuggestions } from '@application/data/field-label-suggestions';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { TRANSPORT_MULTI_PARENT_TARGET_METAS } from '@ui/pages/Events/CreateEvent/components/DynamicForm/presets/transportationPreset';
import AdditionalAttendeesField from '@ui/pages/Events/CreateEvent/components/DynamicForm/components/AdditionalAttendeesField';
// Wrapper component for undertaking field with state management
const UndertakingFieldWrapper = ({ id, onFileChange, onLanguageFilesChange, currentFile, uploadedFileData, uploadedFilesByLanguage, error, helperText, eventHasRegistration, languages }) => {
    const { t } = useTranslation();
    const [allLanguagesHaveFiles, setAllLanguagesHaveFiles] = React.useState(false);
    const handleFileChange = (file) => {
        onFileChange(file);
    };
    const handleFileRemove = () => {
        onFileChange(null);
        setAllLanguagesHaveFiles(false);
    };
    // When editing, pre-populate completion state based on existing uploaded files
    React.useEffect(() => {
        const ids = (languages || []).map((l) => l.id);
        const byLang = uploadedFilesByLanguage || {};
        const hasAll = ids.length > 0 &&
            ids.every((id) => Array.isArray(byLang[id]) && byLang[id].length > 0);
        if (hasAll)
            setAllLanguagesHaveFiles(true);
    }, [languages, uploadedFilesByLanguage]);
    return (_jsxs(Box, { children: [_jsx(UndertakingField, { id: id, label: t('events.formFieldsMenu.undertaking'), required: true, disabled: (Boolean(uploadedFileData) ||
                    Boolean(uploadedFilesByLanguage && Object.keys(uploadedFilesByLanguage || {}).length > 0)) &&
                    Boolean(eventHasRegistration), readonly: (Boolean(uploadedFileData) ||
                    Boolean(uploadedFilesByLanguage && Object.keys(uploadedFilesByLanguage || {}).length > 0)) &&
                    Boolean(eventHasRegistration), value: currentFile, uploadedFileData: uploadedFileData, uploadedByLanguage: uploadedFilesByLanguage, onChange: handleFileChange, onRemove: handleFileRemove, languages: languages, onLanguageFilesChange: (filesByLanguage) => {
                    const ids = (languages || []).map((l) => l.id);
                    const hasAll = ids?.length > 0 &&
                        ids?.every((id) => Array.isArray(filesByLanguage?.[id]) && (filesByLanguage?.[id]?.length || 0) > 0);
                    setAllLanguagesHaveFiles(hasAll);
                    onLanguageFilesChange?.(filesByLanguage);
                }, error: error, helperText: helperText }), error && !allLanguagesHaveFiles && (_jsx(Typography, { variant: 'caption', color: 'error', sx: {
                    display: 'block',
                    mt: 0.5,
                    ml: 1
                }, children: error }))] }));
};
const BRANCHING_FIELD_TYPES = [
    'radio',
    'choice',
    'checkbox-group',
    'dropdown',
    'multi-select',
    'toggle',
    'pickupLocation'
];
export const FORM_FIELD_SCHEMA = {
    'Single Line Input': {
        fieldType: 'text',
        displayParentKey: 'events.formFieldsMenu.singleLineInput',
        descriptionKey: 'events.formFieldsMenu.singleLineInputDescription',
        menuItemId: 'text'
    },
    'Long Text': {
        fieldType: 'multiline',
        displayParentKey: 'events.formFieldsMenu.longText',
        descriptionKey: 'events.formFieldsMenu.longTextDescription',
        menuItemId: 'longText'
    },
    'Choice (Radio)': {
        fieldType: 'radio',
        displayParentKey: 'events.formFieldsMenu.choiceRadio',
        descriptionKey: 'events.formFieldsMenu.choiceRadioDescription',
        menuItemId: 'radio'
    },
    'Choice (Button)': {
        fieldType: 'choice',
        displayParentKey: 'events.formFieldsMenu.choiceButton',
        descriptionKey: 'events.formFieldsMenu.choiceButtonDescription',
        menuItemId: 'choiceButton'
    },
    'Multiple Choice (Checkbox)': {
        fieldType: 'checkbox-group',
        displayParentKey: 'events.formFieldsMenu.multipleChoiceCheckbox',
        descriptionKey: 'events.formFieldsMenu.multipleChoiceCheckboxDescription',
        menuItemId: 'multipleChoice'
    },
    Dropdown: {
        fieldType: 'dropdown',
        displayParentKey: 'events.formFieldsMenu.dropdown',
        descriptionKey: 'events.formFieldsMenu.dropdownDescription',
        menuItemId: 'dropdown'
    },
    'Company Dropdown': {
        fieldType: 'companyDropdown',
        displayParentKey: 'events.formFieldsMenu.companyDropdown',
        descriptionKey: 'events.formFieldsMenu.companyDropdownDescription',
        menuItemId: 'companyDropdown'
    },
    'Multi-Select Dropdown': {
        fieldType: 'multi-select',
        displayParentKey: 'events.formFieldsMenu.multiSelectDropdown',
        descriptionKey: 'events.formFieldsMenu.multiSelectDropdownDescription',
        menuItemId: 'multiSelectDropdown'
    },
    'Toggle (Yes/No)': {
        fieldType: 'toggle',
        displayParentKey: 'events.formFieldsMenu.toggleYesNo',
        descriptionKey: 'events.formFieldsMenu.toggleYesNoDescription',
        menuItemId: 'toggle'
    },
    Undertaking: {
        fieldType: 'undertaking',
        displayParentKey: 'events.formFieldsMenu.undertaking',
        descriptionKey: 'events.formFieldsMenu.undertakingDescription',
        menuItemId: 'undertaking'
    },
    Text: {
        fieldType: 'text',
        displayParentKey: 'events.formFieldsMenu.text',
        descriptionKey: 'events.formFieldsMenu.singleLineInputDescription',
        menuItemId: 'text'
    },
    Email: {
        fieldType: 'email',
        displayParentKey: 'events.formFieldsMenu.email',
        descriptionKey: 'events.formFieldsMenu.singleLineInputDescription',
        menuItemId: 'email'
    },
    Phone: {
        fieldType: 'phone',
        displayParentKey: 'events.formFieldsMenu.phone',
        descriptionKey: 'events.formFieldsMenu.singleLineInputDescription',
        menuItemId: 'phone'
    },
    Date: {
        fieldType: 'date',
        displayParentKey: 'events.formFieldsMenu.date',
        descriptionKey: 'events.formFieldsMenu.singleLineInputDescription',
        menuItemId: 'date'
    },
    Time: {
        fieldType: 'time',
        displayParentKey: 'events.formFieldsMenu.time',
        descriptionKey: 'events.formFieldsMenu.singleLineInputDescription',
        menuItemId: 'time'
    },
    'Pickup Location Dropdown': {
        fieldType: 'pickupLocation',
        displayParentKey: 'events.formFieldsMenu.pickupLocation',
        descriptionKey: 'events.formFieldsMenu.pickupLocationDescription',
        menuItemId: 'pickupLocation'
    },
    'Additional Attendees': {
        fieldType: 'additionalAttendees',
        displayParentKey: 'events.formFieldsMenu.additionalAttendees',
        descriptionKey: 'events.formFieldsMenu.additionalAttendeesDescription',
        menuItemId: 'additionalAttendees'
    }
};
// Helper function to map field types to their corresponding menu item IDs
const getMenuItemIdByFieldType = (fieldType) => {
    const schemaEntry = Object.values(FORM_FIELD_SCHEMA).find((item) => item.fieldType === fieldType);
    const menuItemId = schemaEntry?.menuItemId || 'text';
    return menuItemId;
};
const QuestionCard = ({ id, fieldParent, fieldChild, fieldType, label, descriptionForToolTip, required, options, fileData, uploadedFileData, uploadedFilesByLanguage, undertakingToggleLabel, undertakingAlertNote, error, onFieldTypeChange, onDuplicate, onDelete, onToggleRequired, onUpdateLabel, onUpdateDescriptionForTooltip, onAddOption, onRemoveOption, onUpdateOptionLabel, onFileChange, onOptionShowFieldChange, onBranchingEnabledChange, onUndertakingTextChange, languages, onUndertakingFilesChange, dragHandleProps, helperText, eventHasRegistration, isIdentity, usedIdentityLabels = [], canEdit = true, companyDropdownFields, onCompanyDropdownFieldToggle, onCompanyDropdownFieldLabelUpdate, maxAdditionalAttendees, maxAdditionalAttendeesReachedMessage, additionalAttendeeFields, limitReachedMessage, onMaxAdditionalAttendeesChange, onMaxAdditionalAttendeesReachedMessageChange, onAdditionalAttendeeSubFieldLabelUpdate, onAdditionalAttendeeSubFieldTypeChange, onAdditionalAttendeeSubFieldRequiredChange, onAdditionalAttendeeSubFieldAdd, onAdditionalAttendeeSubFieldRemove, onAdditionalAttendeeSubFieldsReorder, onAdditionalAttendeeSubFieldOptionAdd, onAdditionalAttendeeSubFieldOptionRemove, onAdditionalAttendeeSubFieldOptionLabelUpdate, isExternalEvent = false, showField, stepQuestions = [], claimedFieldIds, parentOptionId, parentOptionLabel, parentFieldLabel, isBranchingEnabled, formFields, hasResponses }) => {
    const theme = useTheme();
    const styles = useQuestionCardStyles();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down(1280));
    const [open, setOpen] = useState(false);
    const handleClick = () => {
        if (isMobile) {
            setOpen((prev) => !prev);
            setTimeout(() => setOpen(false), 2000); // auto-close after 2s
        }
    };
    const { t, i18n } = useTranslation();
    /** Fields that may appear as branching targets on more than one option of the same parent (transport preset). */
    const multiParentBranchingTargetIds = useMemo(() => {
        const ids = new Set();
        formFields?.forEach((f) => {
            const note = f.fieldAdditionalNote;
            const normalized = note?.trim();
            if (normalized && TRANSPORT_MULTI_PARENT_TARGET_METAS.has(normalized)) {
                ids.add(f.id);
            }
        });
        return ids;
    }, [formFields]);
    const [selectFieldTypeBtnAnchorEl, setSelectFieldTypeBtnAnchorEl] = useState(null);
    const [labelSuggestions, setLabelSuggestions] = useState([]);
    const labelInputRef = useRef(null);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    // Track whether the last label update came from a suggestion.
    // Initialize from isIdentity so backend-loaded suggestion labels are respected per card.
    const [lastUpdateFromSuggestion, setLastUpdateFromSuggestion] = useState(!!isIdentity);
    const hasBranchingTargets = (options || []).some((opt) => (opt.optionChainingShowField || []).length > 0);
    const [branchingEnabled, setBranchingEnabled] = useState(() => {
        if (typeof isBranchingEnabled === 'boolean')
            return isBranchingEnabled;
        return hasBranchingTargets;
    });
    React.useEffect(() => {
        if (typeof isBranchingEnabled === 'boolean') {
            setBranchingEnabled(isBranchingEnabled);
            return;
        }
        setBranchingEnabled(hasBranchingTargets);
    }, [isBranchingEnabled, hasBranchingTargets]);
    const optionDefaultValues = (options || []).reduce((acc, opt) => {
        const storedValues = opt.optionChainingShowField ?? [];
        acc[`optionShowField_${id}_${opt.id}`] = storedValues
            .map((val) => {
            const matched = stepQuestions?.find((q) => q.id === val);
            if (matched)
                return { label: matched.label, value: matched.id };
            return null;
        })
            .filter(Boolean);
        return acc;
    }, {});
    const { control, watch, reset, setValue } = useForm({
        defaultValues: {
            toggleLabel: undertakingToggleLabel || '',
            alertText: undertakingAlertNote || '',
            showFieldValue: showField || null,
            ...optionDefaultValues
        }
    });
    // Watch for changes and update parent
    const toggleLabelValue = watch('toggleLabel');
    const alertTextValue = watch('alertText');
    React.useEffect(() => {
        reset({
            toggleLabel: undertakingToggleLabel || '',
            alertText: undertakingAlertNote || '',
            showFieldValue: showField || null
        });
    }, [undertakingToggleLabel, undertakingAlertNote, showField, reset]);
    // Separate effect ONLY for option branching values - use setValue instead of reset
    const lastChangedOptionRef = React.useRef(null);
    React.useEffect(() => {
        (options || []).forEach((opt) => {
            if (lastChangedOptionRef.current === opt.id) {
                lastChangedOptionRef.current = null;
                return;
            }
            const fieldName = `optionShowField_${id}_${opt.id}`;
            const storedValues = opt.optionChainingShowField ?? [];
            const mapped = storedValues
                .map((val) => {
                const matched = stepQuestions?.find((q) => q.id === val);
                if (matched)
                    return { label: matched.label, value: matched.id };
                return null;
            })
                .filter(Boolean);
            setValue(fieldName, mapped);
        });
    }, [options, id, stepQuestions, t, setValue]);
    // Update parent when values change
    React.useEffect(() => {
        onUndertakingTextChange?.(id, toggleLabelValue, alertTextValue);
    }, [toggleLabelValue, alertTextValue, id]);
    const onMenuClose = () => {
        setSelectFieldTypeBtnAnchorEl(null);
    };
    // Lock controls when event has registrations and this is an undertaking with an existing file
    const isUndertakingLocked = fieldType === 'undertaking' && Boolean(uploadedFileData) && Boolean(eventHasRegistration);
    // Additional Attendees' TYPE can never change once created (nested sub-questions wouldn't make
    // sense on any other field type) - but that's unrelated to whether it can be DELETED, which
    // should follow the same hasResponses-based rule every other field already gets (see
    // isDeleteDisabledByResponses below), not be permanently blocked just for being this field type.
    const isAdditionalAttendeesLocked = fieldType === 'additionalAttendees';
    const isLocked = !canEdit || isUndertakingLocked || isAdditionalAttendeesLocked;
    const isDeleteLocked = !canEdit || isUndertakingLocked;
    const isDeleteDisabledByResponses = Boolean(hasResponses);
    // 1. Get schema by fieldParent
    const parentSchema = fieldParent ? FORM_FIELD_SCHEMA[fieldParent] : undefined;
    // 2. Fallback: find by fieldType if parent missing
    const fallbackSchema = !parentSchema
        ? Object.values(FORM_FIELD_SCHEMA).find((item) => item.fieldType === fieldType)
        : undefined;
    const schema = parentSchema || fallbackSchema;
    // 3. Display Parent
    const displayParent = schema ? t(schema.displayParentKey) : fieldParent || '';
    // 4. Display Child
    const displayChild = fieldChild && FORM_FIELD_SCHEMA[fieldChild]
        ? t(FORM_FIELD_SCHEMA[fieldChild].displayParentKey)
        : fieldChild || '';
    const getDescriptionByFieldType = (fieldType) => {
        const schema = Object.values(FORM_FIELD_SCHEMA).find((item) => item.fieldType === fieldType);
        return schema ? t(schema.descriptionKey) : '';
    };
    const tooltipDescription = React.useMemo(() => {
        const desc = String(descriptionForToolTip || '');
        // try to interpret the incoming value as either translated
        // string; for the latter we compare against *all* languages that
        // i18next has loaded, not just the current one.
        const allLangs = i18n.languages || [];
        // 1. match by any known translation
        const matched = Object.values(FORM_FIELD_SCHEMA).find((s) => allLangs.some((lng) => i18n.t(s.descriptionKey, { lng }) === desc));
        if (matched) {
            return t(matched.descriptionKey); // render in current language
        }
        // 3. custom / unknown string – return verbatim
        return desc;
    }, [descriptionForToolTip, i18n, t]);
    const resolvedLabel = React.useMemo(() => {
        const lbl = String(label || '');
        const allLangs = Object.keys(i18n.store?.data || {});
        // Check undertaking first (no interpolation needed)
        const isUndertaking = allLangs.some((lng) => i18n.t('events.undertaking.label', { lng }) === lbl);
        if (isUndertaking)
            return t('events.undertaking.label');
        const allSchemaValues = Object.values(FORM_FIELD_SCHEMA);
        for (const fieldNameSchema of allSchemaValues) {
            for (const lng of allLangs) {
                //  Step 1: translate the fieldName in that language separately
                const translatedFieldName = i18n.t(fieldNameSchema.displayParentKey, { lng });
                //  Step 2: get the newField/newToggle template in that language separately
                const newFieldTemplate = i18n.t('events.formFieldsMenu.newField', { lng });
                const newToggleTemplate = i18n.t('events.formFieldsMenu.newToggle', { lng });
                //  Step 3: manually interpolate — replace {{fieldName}} ourselves
                const asNewField = newFieldTemplate.replace('{{fieldName}}', translatedFieldName);
                const asNewToggle = newToggleTemplate.replace('{{fieldName}}', translatedFieldName);
                if (asNewField === lbl) {
                    return t('events.formFieldsMenu.newField', {
                        fieldName: t(fieldNameSchema.displayParentKey)
                    });
                }
                if (asNewToggle === lbl) {
                    return t('events.formFieldsMenu.newToggle', {
                        fieldName: t(fieldNameSchema.displayParentKey)
                    });
                }
            }
        }
        return lbl;
    }, [label, fieldType, i18n, t]);
    const isBranchableType = BRANCHING_FIELD_TYPES.includes(fieldType);
    const isToggleField = fieldType === 'toggle';
    const isPickupLocationField = fieldType === 'pickupLocation';
    const getCompanySubFieldDisplayLabel = (subField) => {
        const labelKeyById = {
            fullName: 'labels.fullName',
            email: 'labels.email',
            companyName: 'labels.companyName'
        };
        const labelKey = labelKeyById[subField.id];
        if (!labelKey) {
            return t(subField.label, subField.label);
        }
        const rawLabel = String(subField.label ?? '').trim();
        if (!rawLabel)
            return t(labelKey);
        const languages = i18n.languages?.length
            ? i18n.languages
            : Object.keys(i18n.store?.data || {});
        const localizedDefaults = languages.map((lng) => String(i18n.t(labelKey, { lng, defaultValue: '' })).trim());
        const legacyDefaultsById = {
            fullName: ['Full name'],
            email: ['Email'],
            companyName: ['Company name', 'Company']
        };
        const knownDefaults = [
            ...localizedDefaults,
            ...(legacyDefaultsById[subField.id] || [])
        ]
            .map((value) => value.toLowerCase())
            .filter(Boolean);
        return knownDefaults.includes(rawLabel.toLowerCase()) ? t(labelKey) : rawLabel;
    };
    return (_jsxs(Box, { className: 'card__form-input', sx: { ...styles.main }, children: [_jsxs(Box, { className: 'toolbar', sx: { ...styles.toolbar }, children: [_jsx(Tooltip, { title: isDeleteDisabledByResponses
                            ? t('common.deleteFieldHasResponses')
                            : '', arrow: true, placement: 'top', disableHoverListener: !isDeleteDisabledByResponses, slotProps: {
                            tooltip: {
                                sx: {
                                    backgroundColor: theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                    boxShadow: theme.customShadows.dropdown,
                                    fontSize: '13px',
                                    fontWeight: 400,
                                    maxWidth: 240
                                }
                            },
                            arrow: {
                                sx: { color: theme.palette.background.paper }
                            }
                        }, children: _jsx("span", { style: { display: 'inline-flex' }, children: _jsx(IconButton, { className: 'delete-input-field-btn', size: 'medium', color: 'default', onClick: () => {
                                    if (isDeleteLocked || isDeleteDisabledByResponses)
                                        return;
                                    onDelete?.(id);
                                }, disabled: isDeleteLocked || isDeleteDisabledByResponses, sx: {
                                    backgroundColor: (isDeleteLocked || isDeleteDisabledByResponses)
                                        ? theme.palette.action.disabledBackground
                                        : theme.palette.common.grey8,
                                    border: `1px solid ${(isDeleteLocked || isDeleteDisabledByResponses)
                                        ? theme.palette.action.disabled
                                        : theme.palette.action.selected}`,
                                    borderRadius: 2,
                                    width: isSmallScreen ? 28 : 40,
                                    height: isSmallScreen ? 28 : 40,
                                    '&:hover': {
                                        backgroundColor: (isDeleteLocked || isDeleteDisabledByResponses)
                                            ? theme.palette.action.disabledBackground
                                            : theme.palette.grey[50],
                                        borderColor: (isDeleteLocked || isDeleteDisabledByResponses)
                                            ? theme.palette.action.disabled
                                            : theme.palette.grey[400]
                                    }
                                }, children: _jsx(Icon, { icon: 'eva:close-fill', fontSize: isSmallScreen ? '14px' : '24px', color: (isDeleteLocked || isDeleteDisabledByResponses)
                                        ? theme.palette.text.disabled
                                        : theme.palette.text.primary }) }) }) }), _jsxs(Box, { position: 'relative', children: [_jsx(Tooltip, { title: getDescriptionByFieldType(fieldType), arrow: true, placement: isMobile ? 'auto' : 'right', slotProps: {
                                    tooltip: {
                                        sx: {
                                            backgroundColor: theme.palette.background.paper,
                                            color: theme.palette.text.primary,
                                            boxShadow: theme.customShadows.dropdown,
                                            fontSize: '14px',
                                            fontWeight: 400
                                        }
                                    },
                                    arrow: {
                                        sx: {
                                            color: theme.palette.background.paper
                                        }
                                    }
                                }, children: _jsx("span", { style: { display: 'inline-flex' }, children: _jsx(Button, { className: 'form-field-type-menu-btn', variant: 'soft', size: 'medium', sx: {
                                            backgroundColor: theme.palette.common.grey8,
                                            color: theme.palette.text.primary,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            minHeight: 40,
                                            maxWidth: isSmallScreen ? '150px' : 'none',
                                            width: isSmallScreen ? '150px' : 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            '& .MuiButton-endIcon': {
                                                marginLeft: '8px',
                                                flexShrink: 0
                                            },
                                            '& .MuiButton-startIcon': {
                                                marginRight: '8px',
                                                flexShrink: 0
                                            },
                                            '&:hover': {
                                                backgroundColor: theme.palette.grey[50],
                                                borderColor: theme.palette.grey[400]
                                            }
                                        }, startIcon: _jsx(Icon, { icon: 'hugeicons:information-circle', color: theme.palette.text.primary, fontSize: 22 }), endIcon: _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill', fontSize: '22px', color: theme.palette.primary.darker }), onClick: (e) => {
                                            if (isLocked)
                                                return;
                                            setSelectFieldTypeBtnAnchorEl(e.currentTarget);
                                        }, disabled: isLocked, children: _jsxs(Box, { sx: {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1,
                                                minWidth: 0
                                            }, children: [displayParent, " ", displayChild && `→ ${displayChild}`] }) }) }) }), selectFieldTypeBtnAnchorEl && !isLocked && (_jsx(FormFieldsMenu, { anchorPosition: 'right', anchorRef: selectFieldTypeBtnAnchorEl, onClose: onMenuClose, 
                                // Pass the formfields so that unique fields cannot be added again. (email/phone/companyDropdown)
                                formFields: formFields, selectedItemId: getMenuItemIdByFieldType(fieldType), onSelect: ({ parent: selectedParent, child: selectedChild, type, description }) => {
                                    onFieldTypeChange({
                                        id,
                                        fieldParent: selectedParent.label,
                                        fieldChild: selectedChild?.label,
                                        fieldType: type,
                                        descriptionForToolTip: description
                                    });
                                    onMenuClose();
                                } }))] }), !isToggleField && (_jsx(Button, { className: 'required-btn', variant: 'soft', size: 'medium', onClick: () => onToggleRequired?.(id), disabled: fieldType === 'undertaking' || !canEdit, sx: {
                            backgroundColor: theme.palette.common.grey8,
                            color: required ? theme.palette.primary.darker : theme.palette.text.disabled,
                            border: required ? `1px solid ${theme.palette.primary.darker}` : `none`,
                            borderRadius: 1.5,
                            padding: isSmallScreen ? '4px 8px' : '6px 12px',
                            fontSize: isSmallScreen ? 'clamp(10px, 2vw, 12px)' : '14px',
                            fontWeight: 500,
                            textTransform: 'none',
                            minHeight: isSmallScreen ? 28 : 40,
                            '&:hover': {
                                backgroundColor: fieldType === 'undertaking' ? 'inherit' : theme.palette.grey[50],
                                borderColor: fieldType === 'undertaking' ? 'inherit' : theme.palette.grey[400]
                            },
                            '&.Mui-disabled': {
                                backgroundColor: theme.palette.action.disabledBackground,
                                color: theme.palette.action.disabled,
                                border: `1px solid ${theme.palette.grey[700]}`
                            }
                        }, startIcon: _jsx(Box, { sx: {
                                width: isSmallScreen ? 12 : 22,
                                height: isSmallScreen ? 12 : 22,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: fieldType === 'undertaking' && required
                                    ? theme.palette.action.disabled
                                    : theme.palette.grey[600],
                                fontSize: isSmallScreen ? '10px' : '22px',
                                fontWeight: 600
                            }, children: _jsx(Asterisk, { color: fieldType === 'undertaking' && required
                                    ? theme.palette.action.disabled
                                    : required
                                        ? theme.palette.primary.darker
                                        : theme.palette.text.disabled }) }), children: isSmallScreen
                            ? t('events.formFieldsMenu.requiredShort')
                            : t('events.formFieldsMenu.required') })), _jsx(IconButton, { className: 'duplicate-btn', color: 'default', size: 'medium', onClick: () => onDuplicate?.(id), disabled: fieldType === 'undertaking' ||
                            fieldType === 'email' ||
                            fieldType === 'phone' ||
                            fieldType === 'companyDropdown' ||
                            fieldType === 'pickupLocation' ||
                            fieldType === 'additionalAttendees' ||
                            !canEdit, sx: {
                            backgroundColor: theme.palette.common.grey8,
                            border: `1px solid ${theme.palette.action.selected}`,
                            borderRadius: 1.5,
                            width: isSmallScreen ? 28 : 40,
                            height: isSmallScreen ? 28 : 40,
                            '&.Mui-disabled': {
                                backgroundColor: theme.palette.action.disabledBackground,
                                color: theme.palette.text.disabled,
                                border: `1px solid ${theme.palette.grey[300]}`
                            }
                        }, children: _jsx(Icon, { icon: 'hugeicons:copy-01', fontSize: isSmallScreen ? '14px' : '24px', color: fieldType === 'undertaking' ||
                                fieldType === 'email' ||
                                fieldType === 'phone' ||
                                fieldType === 'companyDropdown' ||
                                fieldType === 'pickupLocation' ||
                                fieldType === 'additionalAttendees'
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary }) }), (parentOptionId || parentFieldLabel || parentOptionLabel) && (_jsx(Tooltip, { placement: isMobile ? 'auto' : 'right', open: isMobile ? open : undefined, disableHoverListener: isMobile, disableFocusListener: isMobile, disableTouchListener: isMobile, title: (() => {
                            const prefix = t('events.branchedFrom') || 'Branched from';
                            const parts = [parentFieldLabel, parentOptionLabel].filter(Boolean);
                            return parts.length > 0
                                ? `${prefix} : [${parts.join(' : ')}]`
                                : t('events.branchedField') || 'This field is shown conditionally';
                        })(), arrow: true, children: _jsx(Box, { children: _jsx(IconButton, { className: 'branched-toggle-btn', color: 'default', size: 'medium', onClick: handleClick, sx: {
                                    backgroundColor: alpha(theme.palette.info.main, 0.08),
                                    border: 'none',
                                    borderRadius: 1.5,
                                    width: isSmallScreen ? 28 : 40,
                                    height: isSmallScreen ? 28 : 40,
                                    ':hover': {
                                        backgroundColor: alpha(theme.palette.info.main, 0.08),
                                        border: 'none'
                                    }
                                }, children: _jsx(Icon, { icon: 'material-symbols:arrow-split-rounded', fontSize: isSmallScreen ? '14px' : '24px', color: theme.palette.info.dark }) }) }) })), isBranchableType && (_jsx(Tooltip, { title: branchingEnabled
                            ? t('events.disableBranching') || 'Disable branching'
                            : t('events.enableBranching') || 'Enable branching', arrow: true, children: _jsx("span", { style: { display: 'inline-flex' }, children: _jsx(IconButton, { className: 'branching-toggle-btn', color: 'default', size: 'medium', onClick: () => {
                                    setBranchingEnabled((prev) => {
                                        const enablingBranching = !prev;
                                        // When enabling branching, initialize empty option routes with "No Action".
                                        // This ensures a visible default without affecting already selected routes.
                                        if (enablingBranching) {
                                            (options || []).forEach((opt) => {
                                                const hasStoredValues = Array.isArray(opt.optionChainingShowField) &&
                                                    opt.optionChainingShowField.length > 0;
                                                if (!hasStoredValues) {
                                                    const fieldName = `optionShowField_${id}_${opt.id}`;
                                                    setValue(fieldName, []);
                                                    onOptionShowFieldChange?.(id, opt.id, []);
                                                }
                                            });
                                        }
                                        onBranchingEnabledChange?.(id, enablingBranching);
                                        return enablingBranching;
                                    });
                                }, disabled: !canEdit, sx: {
                                    // Highlighted when branching is active
                                    backgroundColor: theme.palette.common.grey8,
                                    border: branchingEnabled
                                        ? `1px solid ${theme.palette.primary.darker}`
                                        : `1px solid ${theme.palette.action.selected}`,
                                    borderRadius: 1.5,
                                    width: isSmallScreen ? 28 : 40,
                                    height: isSmallScreen ? 28 : 40,
                                    transition: 'background-color 0.2s, border-color 0.2s',
                                    '&:hover': {
                                        backgroundColor: theme.palette.grey[50],
                                        borderColor: theme.palette.grey[400]
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: theme.palette.action.disabledBackground,
                                        color: theme.palette.text.disabled,
                                        border: `1px solid ${theme.palette.grey[300]}`
                                    }
                                }, children: _jsx(Icon, { icon: 'material-symbols:arrow-split-rounded', fontSize: isSmallScreen ? '14px' : '24px', color: branchingEnabled ? theme.palette.primary.darker : theme.palette.text.disabled }) }) }) }))] }), _jsxs(Box, { className: 'field-container', sx: { ...styles.fieldContainer }, children: [fieldType !== 'undertaking' && (_jsxs(_Fragment, { children: [_jsx(EditableField, { ref: labelInputRef, value: resolvedLabel, bg: 'light', onValueChange: (newLabel) => {
                                    const lang = (i18n.language || 'en').split('-')[0];
                                    const suggestions = getFieldLabelSuggestions({
                                        lang,
                                        query: newLabel || '',
                                        fieldType,
                                        limit: 5
                                    });
                                    // Hide suggestions that are already used as identity labels on ANY card.
                                    const usedSet = new Set(usedIdentityLabels.map((lbl) => String(lbl).trim().toLowerCase()));
                                    const filteredSuggestions = suggestions.filter((s) => !usedSet.has(s.label.trim().toLowerCase()));
                                    setLabelSuggestions(filteredSuggestions);
                                    setSuggestionsOpen(filteredSuggestions.length > 0);
                                    setLastUpdateFromSuggestion(false);
                                }, editableFieldSx: {
                                    width: '100%',
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 0,
                                    color: theme.palette.text.primary,
                                    ...theme.typography.body1,
                                    // kill MUI default filled styles
                                    '& .MuiFilledInput-root': {
                                        boxShadow: 'none',
                                        '&.Mui-focused': {
                                            backgroundColor: 'transparent'
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent'
                                        },
                                        '&:before, &:after': {
                                            display: 'none'
                                        }
                                    }
                                }, inputSx: {
                                    paddingLeft: 0,
                                    height: 25.5,
                                    '& input': {
                                        ...theme.typography.body1,
                                        fontWeight: 400,
                                        color: theme.palette.text.primary,
                                        padding: '0px',
                                        '&::placeholder': {
                                            ...theme.typography.body1,
                                            opacity: 1,
                                            color: theme.palette.text.disabled
                                        }
                                    }
                                }, onConfirmValue: (newLabel) => {
                                    // Always call onUpdateLabel - it will handle validation and set error
                                    onUpdateLabel?.(id, newLabel, lastUpdateFromSuggestion, undefined);
                                }, onResetValue: () => onUpdateLabel?.(id, label?.toString() || '', lastUpdateFromSuggestion, undefined) }), _jsx(EditableField
                            // ref={labelInputRef}
                            , { 
                                // ref={labelInputRef}
                                value: tooltipDescription, bg: 'light', placeholder: getDescriptionByFieldType(fieldType), onValueChange: (newDescription) => onUpdateDescriptionForTooltip?.(id, newDescription), editableFieldSx: {
                                    width: '100%',
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 0,
                                    color: theme.palette.text.secondary,
                                    ...theme.typography.body2,
                                    // kill MUI default filled styles
                                    '& .MuiFilledInput-root': {
                                        boxShadow: 'none',
                                        '&.Mui-focused': {
                                            backgroundColor: 'transparent',
                                            borderRadius: '14px'
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent'
                                        },
                                        '&:before, &:after': {
                                            display: 'none'
                                        }
                                    }
                                }, inputSx: {
                                    paddingLeft: 0,
                                    height: 25.5,
                                    '& input': {
                                        ...theme.typography.body2,
                                        fontWeight: 400,
                                        color: theme.palette.text.secondary,
                                        padding: '0px',
                                        '&::placeholder': {
                                            ...theme.typography.body2,
                                            opacity: 1,
                                            color: theme.palette.text.disabled
                                        }
                                    }
                                }, onConfirmValue: (newDescription) => {
                                    // Always call onUpdateDescriptionForTooltip - it will handle validation and set error
                                    onUpdateDescriptionForTooltip?.(id, newDescription);
                                }, onResetValue: () => onUpdateDescriptionForTooltip?.(id, descriptionForToolTip || '') })] })), error && fieldType !== 'undertaking' && (_jsx(Typography, { variant: 'caption', color: 'error', sx: {
                            display: 'block',
                            mt: 0.5,
                            ml: 1
                        }, children: error })), _jsx(Popper, { open: suggestionsOpen && fieldType !== 'undertaking' && labelSuggestions.length > 0, anchorEl: labelInputRef.current, placement: 'bottom-start', style: { zIndex: 1300 }, children: _jsx(ClickAwayListener, { onClickAway: () => setSuggestionsOpen(false), children: _jsx(Paper, { elevation: 3, sx: {
                                    mt: 0.5,
                                    maxHeight: 240,
                                    overflowY: 'auto',
                                    borderRadius: 1,
                                    border: `1px solid ${theme.palette.grey[300]}`
                                }, children: _jsx(List, { dense: true, disablePadding: true, children: labelSuggestions.map((s) => (_jsx(ListItemButton, { onMouseDown: (e) => {
                                            // prevent input blur before click handler
                                            e.preventDefault();
                                        }, onClick: () => {
                                            onUpdateLabel?.(id, s.label, true, s.id);
                                            setLastUpdateFromSuggestion(true);
                                            setSuggestionsOpen(false);
                                            setLabelSuggestions([]);
                                        }, children: _jsx(Typography, { variant: 'body2', children: s.label }) }, s.id + s.label))) }) }) }) }), fieldType === 'undertaking' ? (_jsx(_Fragment, { children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: [_jsx(UndertakingFieldWrapper, { id: id, currentFile: fileData, uploadedFileData: uploadedFileData, uploadedFilesByLanguage: uploadedFilesByLanguage, onFileChange: (file) => onFileChange?.(id, file), onLanguageFilesChange: (filesByLanguage) => onUndertakingFilesChange?.(id, filesByLanguage), eventHasRegistration: eventHasRegistration, error: error, helperText: helperText, languages: languages }), _jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', name: 'toggleLabel', rows: 4, multiline: true, control: control, label: t('events.createNewEvent.toggleText') }), _jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', rows: 4, multiline: true, name: 'alertText', control: control, label: t('events.createNewEvent.alertText') })] }) })) : fieldType === 'companyDropdown' && isExternalEvent ? (_jsxs(Box, { sx: { mt: 2 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 2 }, children: [_jsx(Typography, { variant: 'body2', sx: { fontWeight: 500, color: theme.palette.text.primary }, children: t('events.referredBy') }), _jsx(InfoIconTooltip, { title: t('events.createNewEvent.tooltips.companyDropdownExternalsAllowed'), variant: 'light', size: 'large' }), _jsxs(Box, { sx: {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.25,
                                            cursor: canEdit ? 'grab' : 'default',
                                            opacity: 0.6,
                                            ml: 'auto'
                                        }, children: [_jsx(Box, { sx: {
                                                    width: 4,
                                                    height: 4,
                                                    borderRadius: '50%',
                                                    bgcolor: theme.palette.grey[400]
                                                } }), _jsx(Box, { sx: {
                                                    width: 4,
                                                    height: 4,
                                                    borderRadius: '50%',
                                                    bgcolor: theme.palette.grey[400]
                                                } }), _jsx(Box, { sx: {
                                                    width: 4,
                                                    height: 4,
                                                    borderRadius: '50%',
                                                    bgcolor: theme.palette.grey[400]
                                                } })] })] }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: companyDropdownFields?.map((subField) => (_jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.grey[300]}`,
                                        backgroundColor: theme.palette.common.white
                                    }, children: [_jsxs(Box, { sx: {
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.25,
                                                cursor: canEdit ? 'grab' : 'default',
                                                opacity: 0.6,
                                                mr: 0.5
                                            }, children: [_jsx(Box, { sx: {
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        bgcolor: theme.palette.grey[400]
                                                    } }), _jsx(Box, { sx: {
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        bgcolor: theme.palette.grey[400]
                                                    } }), _jsx(Box, { sx: {
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        bgcolor: theme.palette.grey[400]
                                                    } })] }), _jsxs(Box, { sx: {
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                flex: 1
                                            }, children: [_jsx(EditableField, { bg: 'grey8', value: getCompanySubFieldDisplayLabel(subField), onConfirmValue: (newLabel) => {
                                                        // Always call onCompanyDropdownFieldLabelUpdate - it will handle validation and set error
                                                        onCompanyDropdownFieldLabelUpdate?.(id, subField.id, newLabel);
                                                    }, onResetValue: () => onCompanyDropdownFieldLabelUpdate?.(id, subField.id, subField.label), editableFieldSx: {
                                                        width: '100%',
                                                        height: 40,
                                                        borderRadius: 2,
                                                        border: `1px solid ${theme.palette.grey[400]}`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 2,
                                                        color: theme.palette.grey[400],
                                                        fontSize: '14px',
                                                        ...theme.typography.body1,
                                                        lineHeight: 28 / 16,
                                                        '& .MuiFilledInput-root': {
                                                            borderRadius: 2,
                                                            boxShadow: 'none',
                                                            '&.Mui-focused': {
                                                                backgroundColor: theme.palette.common.grey8,
                                                                borderRadius: 1
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.common.grey8
                                                            },
                                                            '&:before, &:after': {
                                                                display: 'none'
                                                            }
                                                        }
                                                    }, inputSx: {
                                                        '& input': {
                                                            ...theme.typography.body1,
                                                            fontWeight: 400,
                                                            color: theme.palette.text.primary,
                                                            padding: '0px',
                                                            '&::placeholder': {
                                                                ...theme.typography.body1,
                                                                opacity: 1,
                                                                color: theme.palette.text.disabled
                                                            }
                                                        }
                                                    } }), subField.error && (_jsx(Typography, { variant: 'caption', color: 'error', sx: {
                                                        display: 'block',
                                                        mt: 0.5,
                                                        ml: 1
                                                    }, children: subField.error && subField.errorField
                                                        ? t(subField.error, { field: subField.errorField }) || subField.error
                                                        : subField.error })), _jsxs(Box, { sx: { display: 'flex', gap: 2, alignItems: 'center', mt: 0.5 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Typography, { variant: 'caption', sx: { color: theme.palette.text.secondary }, children: t('events.formFieldsMenu.enabled') }), _jsx(Switch, { size: 'small', checked: subField.enabled, onChange: () => onCompanyDropdownFieldToggle?.(id, subField.id, 'enabled'), disabled: !canEdit || subField.id === 'fullName' || subField.id === 'companyName', sx: {
                                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                                            color: theme.palette.primary.main
                                                                        },
                                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                            backgroundColor: theme.palette.primary.main
                                                                        }
                                                                    } })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Typography, { variant: 'caption', sx: { color: theme.palette.text.secondary }, children: t('events.formFieldsMenu.required') }), _jsx(Switch, { size: 'small', checked: subField.required, onChange: () => onCompanyDropdownFieldToggle?.(id, subField.id, 'required'), disabled: !canEdit ||
                                                                        !subField.enabled ||
                                                                        subField.id === 'fullName' ||
                                                                        subField.id === 'companyName', sx: {
                                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                                            color: theme.palette.primary.main
                                                                        },
                                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                            backgroundColor: theme.palette.primary.main
                                                                        }
                                                                    } })] })] })] })] }, subField.id))) })] })) : fieldType === 'additionalAttendees' ? (_jsx(AdditionalAttendeesField, { fieldId: id, maxAdditionalAttendees: maxAdditionalAttendees ?? 2, maxAdditionalAttendeesReachedMessage: maxAdditionalAttendeesReachedMessage ?? '', subFields: additionalAttendeeFields || [], formFields: formFields, canEdit: canEdit, disabledByResponses: isDeleteDisabledByResponses, onMaxAttendeesChange: (fieldId, max) => onMaxAdditionalAttendeesChange?.(fieldId, max), onMaxAttendeesReachedMessageChange: (fieldId, message) => onMaxAdditionalAttendeesReachedMessageChange?.(fieldId, message), onSubFieldLabelUpdate: (fieldId, subFieldId, label) => onAdditionalAttendeeSubFieldLabelUpdate?.(fieldId, subFieldId, label), onSubFieldTypeChange: (fieldId, subFieldId, type, parent, child) => onAdditionalAttendeeSubFieldTypeChange?.(fieldId, subFieldId, type, parent, child), onSubFieldRequiredChange: (fieldId, subFieldId, required) => onAdditionalAttendeeSubFieldRequiredChange?.(fieldId, subFieldId, required), onSubFieldAdd: (fieldId, selection) => onAdditionalAttendeeSubFieldAdd?.(fieldId, selection), onSubFieldRemove: (fieldId, subFieldId) => onAdditionalAttendeeSubFieldRemove?.(fieldId, subFieldId), onSubFieldsReorder: (fieldId, activeId, overId) => onAdditionalAttendeeSubFieldsReorder?.(fieldId, activeId, overId), onSubFieldOptionAdd: (fieldId, subFieldId) => onAdditionalAttendeeSubFieldOptionAdd?.(fieldId, subFieldId), onSubFieldOptionRemove: (fieldId, subFieldId, optionId) => onAdditionalAttendeeSubFieldOptionRemove?.(fieldId, subFieldId, optionId), onSubFieldOptionLabelUpdate: (fieldId, subFieldId, optionId, label) => onAdditionalAttendeeSubFieldOptionLabelUpdate?.(fieldId, subFieldId, optionId, label) })) : BRANCHING_FIELD_TYPES.includes(fieldType) ? (_jsxs(Box, { children: [options?.map((opt) => {
                                const optionShowFieldName = `optionShowField_${id}_${opt.id}`;
                                const steps = stepQuestions?.map((question) => ({
                                    label: question.label,
                                    value: question.id
                                })) ?? [];
                                const showFieldOptions = steps;
                                // Build values selected by OTHER options on THIS same field (not current opt)
                                const selectedByOtherOptions = (options || [])
                                    .filter((o) => o.id !== opt.id)
                                    .flatMap((o) => o.optionChainingShowField || []);
                                // filteredShowFieldOptions:
                                // - selected by OTHER fields (claimedFieldIds): filter OUT completely
                                // - selected by OTHER OPTIONS on same field: show but disabled
                                // - everything else: show normally
                                const filteredShowFieldOptions = showFieldOptions
                                    .filter((o) => !claimedFieldIds?.has(o.value))
                                    .map((o) => {
                                    const targetId = o.value;
                                    const selectedElsewhereOnSameField = selectedByOtherOptions.includes(targetId);
                                    const allowRepeatAcrossOptions = multiParentBranchingTargetIds.has(targetId);
                                    return {
                                        ...o,
                                        isDisabled: selectedElsewhereOnSameField && !allowRepeatAcrossOptions
                                    };
                                });
                                return (_jsx(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                        py: 1.5,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        '&:last-child': {
                                            borderBottom: 'none'
                                        }
                                    }, children: _jsxs(Grid, { container: true, size: 12, spacing: 1, sx: { flex: 1, minWidth: 0, alignItems: 'center' }, children: [_jsx(Grid, { size: { xs: 12, sm: branchingEnabled ? 6 : 12 }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }, children: [!isToggleField && !isPickupLocationField && (_jsx(Tooltip, { title: isDeleteDisabledByResponses
                                                                ? t('common.deleteFieldHasResponses')
                                                                : '', arrow: true, placement: 'top', disableHoverListener: !isDeleteDisabledByResponses, slotProps: {
                                                                tooltip: {
                                                                    sx: {
                                                                        backgroundColor: theme.palette.background.paper,
                                                                        color: theme.palette.text.primary,
                                                                        boxShadow: theme.customShadows.dropdown,
                                                                        fontSize: '13px',
                                                                        fontWeight: 400,
                                                                        maxWidth: 240
                                                                    }
                                                                },
                                                                arrow: {
                                                                    sx: { color: theme.palette.background.paper }
                                                                }
                                                            }, children: _jsx("span", { style: { display: 'inline-flex' }, children: _jsx(IconButton, { size: 'small', color: 'default', onClick: () => onRemoveOption?.(id, opt.id), disabled: !canEdit || isDeleteDisabledByResponses, sx: {
                                                                        border: 'none',
                                                                        background: (theme) => theme.palette.common.white,
                                                                        flexShrink: 0
                                                                    }, children: _jsx(Icon, { icon: 'eva:close-fill', fontSize: '18px', color: theme.palette.text.primary }) }) }) })), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }, children: [isToggleField ? (_jsxs(Box, { display: 'flex', gap: 1, style: {}, alignItems: 'center', children: [_jsxs(Box, { children: [" ", _jsx(Switch, { checked: String(opt.value).toLowerCase() === '1' ||
                                                                                        String(opt.value).toLowerCase() === 'true', disabled: true, sx: {
                                                                                        width: 33,
                                                                                        padding: '9px 0',
                                                                                        '& .MuiSwitch-switchBase': {
                                                                                            left: '-8px'
                                                                                        }
                                                                                    } })] }), _jsx(Typography, { variant: 'body2', color: 'text.disabled', children: opt.label })] })) : isPickupLocationField ? (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: opt.label })) : (_jsx(EditableField, { bg: 'grey8', value: opt.label, onConfirmValue: (newLabel) => {
                                                                        onUpdateOptionLabel?.(id, opt.id, newLabel);
                                                                    }, onResetValue: () => onUpdateOptionLabel?.(id, opt.id, opt.label), editableFieldSx: {
                                                                        ...theme.typography.body2,
                                                                        fontWeight: 400,
                                                                        lineHeight: 28 / 16
                                                                    }, inputSx: {
                                                                        '& input': {
                                                                            ...theme.typography.body2,
                                                                            fontWeight: 400,
                                                                            color: theme.palette.text.secondary,
                                                                            padding: '0px',
                                                                            '&::placeholder': {
                                                                                ...theme.typography.body2,
                                                                                opacity: 1,
                                                                                color: theme.palette.text.disabled
                                                                            }
                                                                        }
                                                                    } })), opt.error && (_jsx(Typography, { variant: 'caption', color: 'error', sx: {
                                                                        display: 'block',
                                                                        mt: 0.5,
                                                                        ml: 1
                                                                    }, children: opt.error && opt.errorField
                                                                        ? t(opt.error, { field: opt.errorField }) || opt.error
                                                                        : opt.error }))] })] }) }), branchingEnabled && (_jsx(Grid, { size: { xs: 12, sm: 6 }, sx: { '& .MuiFilledInput-root': { height: 'auto' } }, children: _jsx(ControlledHCheckboxAutocomplete, { label: t('events.show'), hidePlaceholderWhenValue: true, labelTooltipText: t('events.showFieldTooltip'), name: optionShowFieldName, control: control, options: filteredShowFieldOptions, disabled: !canEdit, placeholder: t('events.selectSteps'), bg: 'grey8', maxTags: 1, selectedChipVariant: 'soft', selectedChipColor: 'inherit', selectedChipSx: {
                                                        '& .MuiChip-deleteIcon': {
                                                            color: 'inherit'
                                                        }
                                                    }, showCountTextWhenMultiple: true, checkboxMode: true, treatDisabledAsMandatory: false, onChange: (selectedValues) => {
                                                        lastChangedOptionRef.current = opt.id;
                                                        onOptionShowFieldChange?.(id, opt.id, selectedValues.map((v) => v.value));
                                                    } }) }))] }) }, opt.id));
                            }), !isToggleField && !isPickupLocationField && (_jsx(Button, { variant: 'text', size: 'small', onClick: () => onAddOption?.(id), disabled: !canEdit || isDeleteDisabledByResponses, sx: {
                                    color: theme.palette.primary.darker,
                                    ...theme.typography.body2,
                                    fontWeight: 400,
                                    lineHeight: 22 / 14,
                                    mt: 1
                                }, startIcon: _jsx(AddCircle, { color: theme.palette.primary.darker, fontSize: '20px' }), children: t('common.addMore') }))] })) : fieldType === 'pickupLocation' ? (_jsx(Box, { children: (options || []).length > 0 ? ((options || [])
                            .filter((opt) => !opt?.isClosed)
                            .map((opt) => (_jsx(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 1.25,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                '&:last-child': { borderBottom: 'none' }
                            }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: opt.label }) }, opt.id)))) : (_jsx(Typography, { variant: 'caption', color: 'text.secondary', sx: { ml: 0.5 }, children: t('events.transportPreset.noPickupLocationsAvailable') })) })) : (_jsx(_Fragment, {}))] }), canEdit && (_jsx(Box, { className: 'absolute-drag-indicator', sx: {
                    ...styles.dragIndicatorContainer,
                    cursor: dragHandleProps ? 'grab' : 'not-allowed'
                }, ...(dragHandleProps || {}), children: _jsx(Icon, { icon: 'material-symbols:drag-indicator', fontSize: '16px', color: dragHandleProps ? theme.palette.grey[600] : theme.palette.grey[300] }) }))] }));
};
export default QuestionCard;
